'use client';

import Link from 'next/link';
import { company } from '@/lib/company';

/**
 * Thin credibility rail. Sits directly under the hero so the first thing a
 * reviewer reads after the wordmark is a fact they can verify against the
 * Ontario corporate register, not a slogan.
 *
 * Layout follows DESIGN.md: hairline rules, mono metadata voice, no cards.
 */
export function CorporateStrip() {
  const items: Array<{ label: string; value: string }> = [
    { label: 'Legal entity', value: company.legalName },
    { label: 'Jurisdiction', value: company.incorporatedIn },
    { label: 'Corporation no.', value: company.corporationNumber },
    { label: 'Registered office', value: `${company.address.city}, ${company.address.regionCode}` },
  ];

  return (
    <section
      className="self-styled relative z-20 border-y border-white/10"
      aria-label="Corporate identity"
    >
      <div className="content-padding">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 py-8 md:grid-cols-4 md:py-9">
          {items.map((item) => (
            <div key={item.label} className="flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                {item.label}
              </span>
              <span className="text-sm font-light leading-snug text-white/85 md:text-[15px]">
                {item.value}
              </span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/[0.07] py-5">
          <Link
            href="/company"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white/50 transition-colors hover:text-white"
          >
            Full corporate details
            <span
              aria-hidden="true"
              className="inline-block transition-transform duration-300 group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
